import React from 'react';
import { Card, CardContent, Box, Typography, Avatar, Tooltip } from '@mui/material';
import { tint } from '../theme/accents';

// The one figure card used on every page. Each instance takes its domain's
// accent: a bar down the edge, a tinted icon, the number in that hue.
//
// Two things it deliberately guarantees, because both were breaking rows before:
//  - the label can't make one card taller than its neighbours and leave the row
//    ragged: it stays on one line, except on a phone where half the card is
//    icon - there it gets two lines, always reserved, so every card matches;
//  - the wash is faint. Five saturated gradients side by side read as a paint
//    chart rather than a summary, so colour is carried by the bar, icon and
//    figure instead of the whole surface.
const StatCard = ({ title, value, subtitle, icon, accent, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: tint(accent, 0.3),
      background: (t) => `linear-gradient(150deg, ${tint(accent, 0.07)} 0%, ${t.palette.background.paper} 60%)`,
      transition: 'transform .18s ease, border-color .18s ease',
      '&:before': {
        content: '""',
        position: 'absolute',
        insetBlock: 0,
        left: 0,
        width: 3,
        background: accent,
      },
      '&:hover': {
        borderColor: tint(accent, 0.55),
        transform: onClick ? 'translateY(-2px)' : 'none',
      },
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 }, height: '100%', p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 3 } } }}>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Tooltip title={title} enterDelay={600}>
          <Typography
            variant="overline"
            display="block"
            sx={{
              color: 'text.secondary',
              letterSpacing: '.08em',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: { xs: 1.4, sm: 2.66 },
              minHeight: { xs: '2.8em', sm: 'auto' },
            }}
          >
            {title}
          </Typography>
        </Tooltip>
        <Typography
          component="div"
          className="tabular"
          sx={{
            mt: 0.25,
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            // Scales down on narrow cards so a six-figure sum can't overflow.
            fontSize: 'clamp(1.35rem, 2.2vw, 1.9rem)',
            lineHeight: 1.2,
            color: accent,
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {icon && (
        <Avatar
          sx={{
            bgcolor: tint(accent, 0.16),
            color: accent,
            border: '1px solid',
            borderColor: tint(accent, 0.35),
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
      )}
    </CardContent>
  </Card>
);

export default StatCard;
